import {
	AlertOutlined,
	ArrowUpOutlined,
	PlusOutlined,
	SendOutlined,
	WalletOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DatePicker, Form, Input, InputNumber, Modal, message, Select, Spin } from "antd";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import type { AppRole } from "@/shared/config/navigation";
import { canTopupSmsBalance } from "@/shared/config/permissions";
import { parseApiError } from "@/shared/utils/error";
import { SmsNavigation } from "../components/SmsNavigation";
import {
	type BalanceTransactionsListParams,
	getBalanceTransactions,
	getSmsBalance,
	topupSmsBalance,
} from "../services/sms.api";
import type { BalanceTransaction, TopupMethod } from "../types/sms.types";

function formatNum(n: number): string {
	const abs = Math.abs(n);
	const hasFraction = abs % 1 !== 0;
	const isSmall = abs < 1000;
	const formatted =
		hasFraction || isSmall
			? abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")
			: abs.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
	return n < 0 ? `\u2212${formatted}` : formatted;
}

const topupMethodOptions = [
	{ label: "Bank o'tkazmasi", value: "bank" as TopupMethod },
	{ label: "Karta", value: "karta" as TopupMethod },
	{ label: "Naqd pul", value: "naqd" as TopupMethod },
];

function toDateTimeParam(date: string, time: "00:00:00" | "23:59:59") {
	return date ? `${date}T${time}+05:00` : undefined;
}

function buildTransactionsParams(
	page: number,
	limit: number,
	typeFilter: "all" | "topup" | "spend" | "refund",
	dateFrom: string,
	dateTo: string
): BalanceTransactionsListParams {
	const entries: [
		keyof BalanceTransactionsListParams,
		BalanceTransactionsListParams[keyof BalanceTransactionsListParams],
	][] = [
		["page", page],
		["limit", limit],
		["type", typeFilter === "all" ? undefined : typeFilter],
		["date_from", toDateTimeParam(dateFrom, "00:00:00")],
		["date_to", toDateTimeParam(dateTo, "23:59:59")],
	];

	return Object.fromEntries(
		entries.filter(([, value]) => value !== undefined)
	) as BalanceTransactionsListParams;
}

function TransactionRows({ transactions }: { transactions: BalanceTransaction[] }) {
	if (transactions.length === 0) {
		return (
			<tr>
				<td colSpan={5} style={{ textAlign: "center", padding: "30px 10px" }}>
					<span style={{ color: "var(--crm-ink-3)" }}>Harakatlar topilmadi</span>
				</td>
			</tr>
		);
	}

	return transactions.map((tx) => (
		<tr key={tx.id}>
			<td>{tx.date}</td>
			<td>
				<span className={`crm-badge ${tx.type === "topup" ? "green" : "slate"}`}>
					{tx.type === "topup" ? "To'ldirish" : "Sarf"}
				</span>
			</td>
			<td style={{ textAlign: "right" }}>
				<span className="num" style={{ color: tx.amount >= 0 ? "#389e0d" : "#cf1322" }}>
					{tx.amount >= 0 ? "+" : ""}
					{formatNum(tx.amount)}
				</span>
			</td>
			<td style={{ textAlign: "right" }}>
				<span className="num">{formatNum(tx.balanceAfter)}</span>
			</td>
			<td>{tx.note}</td>
		</tr>
	));
}

export function SmsBalansPage() {
	const queryClient = useQueryClient();
	const { role } = useOutletContext<{ role: AppRole }>();
	const canTopupBalance = canTopupSmsBalance(role);
	const [topupOpen, setTopupOpen] = useState(false);
	const [form] = Form.useForm();
	const [currentPage, setCurrentPage] = useState(1);
	const [typeFilter, setTypeFilter] = useState<"all" | "topup" | "spend" | "refund">("all");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const pageSize = 10;

	// 1. Fetch SMS Balance
	const { data: balance, isLoading: balanceLoading } = useQuery({
		queryKey: ["smsBalance"],
		queryFn: getSmsBalance,
	});

	// 2. Fetch Balance Transactions (real endpoint)
	const { data: txData, isLoading: txLoading } = useQuery({
		queryKey: ["smsBalanceTransactions", currentPage, typeFilter, dateFrom, dateTo],
		queryFn: () =>
			getBalanceTransactions(
				buildTransactionsParams(currentPage, pageSize, typeFilter, dateFrom, dateTo)
			),
	});

	const transactions = useMemo(() => txData?.items ?? [], [txData]);
	const totalTx = txData?.meta?.total ?? 0;
	const totalPages = Math.ceil(totalTx / pageSize);

	// 3. Topup mutation
	const topupMutation = useMutation({
		mutationFn: (values: {
			amount: number;
			method: TopupMethod;
			documentNumber: string;
			note: string;
		}) => topupSmsBalance(values),
		onSuccess: (_newBalance, values) => {
			message.success(`Balans to'ldirildi: +${formatNum(values.amount)} so'm`);
			queryClient.invalidateQueries({ queryKey: ["smsBalance"] });
			queryClient.invalidateQueries({ queryKey: ["smsBalanceTransactions"] });
			setTopupOpen(false);
			form.resetFields();
			setCurrentPage(1);
		},
		onError: (err) => {
			message.error(parseApiError(err) || "Balansni to'ldirishda xatolik yuz berdi");
		},
	});

	const handleTopup = () => {
		if (!canTopupBalance) {
			message.warning("Bu rol SMS balansini to'ldira olmaydi.");
			return;
		}

		form.validateFields().then((values) => {
			topupMutation.mutate({
				amount: values.amount,
				method: values.method,
				documentNumber: values.documentNumber || "",
				note: values.note || "",
			});
		});
	};

	if (balanceLoading) {
		return (
			<div style={{ textAlign: "center", padding: "100px 0" }}>
				<Spin size="large" />
				<div style={{ marginTop: "16px", color: "var(--crm-ink-3)" }}>
					SMS ma'lumotlari yuklanmoqda...
				</div>
			</div>
		);
	}

	// Calculate warning threshold
	const currentBal = balance?.currentBalance ?? 0;
	const warningThr = balance?.warningThreshold ?? 500;
	const pricePerSms = balance?.pricePerSms ?? 0.5;
	const estimatedSms = Math.floor(currentBal / pricePerSms);

	const warningStatus =
		currentBal < warningThr
			? { label: "Kam!", color: "red" as const }
			: { label: "OK", color: "green" as const };

	return (
		<div className="crm-sms-balans">
			{/* Page Head */}
			<div className="crm-page-head" style={{ marginBottom: "16px" }}>
				<div>
					<h1>SMS moduli</h1>
					<p>Eskiz integratsiyasi orqali tizim balansi va bildirishnomalarni boshqarish</p>
				</div>
				<div className="crm-head-actions">
					<Button
						className="crm-primary-btn"
						icon={<PlusOutlined />}
						type="primary"
						onClick={() => {
							if (!canTopupBalance) {
								message.warning("Bu rol SMS balansini to'ldira olmaydi.");
								return;
							}
							setTopupOpen(true);
						}}
					>
						Balansni to'ldirish
					</Button>
				</div>
			</div>

			{/* Sub-Navigation Tabs */}
			<SmsNavigation activeTab="balance" />

			{/* KPI Grid */}
			<div className="crm-kpi-grid crm-sms-kpi-grid">
				<div className="crm-kpi crm-sms-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon green">
							<WalletOutlined />
						</span>
						Joriy balans
					</div>
					<div className="crm-kpi-value">
						<strong>{formatNum(currentBal)}</strong>
						<span>so'm</span>
					</div>
					<div className="crm-kpi-note">≈ {formatNum(estimatedSms)} SMS</div>
				</div>

				<div className="crm-kpi crm-sms-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon blue">
							<ArrowUpOutlined />
						</span>
						Jami to'ldirilgan
					</div>
					<div className="crm-kpi-value">
						<strong>{formatNum(balance?.totalTopup ?? 0)}</strong>
						<span>so'm</span>
					</div>
				</div>

				<div className="crm-kpi crm-sms-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon sky">
							<SendOutlined />
						</span>
						Jami sarflangan
					</div>
					<div className="crm-kpi-value">
						<strong>{formatNum(balance?.totalSpent ?? 0)}</strong>
						<span>so'm</span>
					</div>
				</div>

				<div className="crm-kpi crm-sms-kpi">
					<div className="crm-kpi-title">
						<span className="crm-kpi-icon amber">
							<AlertOutlined />
						</span>
						Ogohlantirish
					</div>
					<div className="crm-kpi-value">
						<strong style={{ color: warningStatus.color === "red" ? "#cf1322" : "#389e0d" }}>
							{warningStatus.label}
						</strong>
					</div>
					<div className="crm-kpi-note" style={{ color: "var(--crm-ink-3)" }}>
						&lt; {formatNum(Math.floor(warningThr / pricePerSms))} SMS qolganda
					</div>
				</div>
			</div>

			{/* Transactions Table */}
			<div className="crm-card crm-table-card crm-sms-table-card" style={{ marginTop: "20px" }}>
				<div className="crm-sms-table-top">
					<div className="crm-card-head">
						<h3>Balans harakatlari tarixi</h3>
					</div>
					<div className="crm-payment-toolbar crm-sms-balance-filters">
						<Select
							aria-label="Harakat turi"
							options={[
								{ label: "Barchasi", value: "all" },
								{ label: "To'ldirish", value: "topup" },
								{ label: "Sarf", value: "spend" },
								{ label: "Qaytarish", value: "refund" },
							]}
							style={{ minWidth: 160 }}
							value={typeFilter}
							onChange={(value) => {
								setCurrentPage(1);
								setTypeFilter(value);
							}}
						/>
						<DatePicker
							aria-label="Boshlanish sanasi"
							format="YYYY-MM-DD"
							placeholder="Boshlanish sanasi"
							placement="bottomLeft"
							popupClassName="crm-compact-date-popup"
							value={dateFrom ? dayjs(dateFrom) : null}
							onChange={(_, value) => {
								setCurrentPage(1);
								setDateFrom(Array.isArray(value) ? "" : value || "");
							}}
						/>
						<DatePicker
							aria-label="Tugash sanasi"
							format="YYYY-MM-DD"
							placeholder="Tugash sanasi"
							placement="bottomLeft"
							popupClassName="crm-compact-date-popup"
							value={dateTo ? dayjs(dateTo) : null}
							onChange={(_, value) => {
								setCurrentPage(1);
								setDateTo(Array.isArray(value) ? "" : value || "");
							}}
						/>
						<Button
							onClick={() => {
								setCurrentPage(1);
								setTypeFilter("all");
								setDateFrom("");
								setDateTo("");
							}}
						>
							Tozalash
						</Button>
					</div>
				</div>
				<div className="crm-table-wrap">
					{txLoading ? (
						<div style={{ textAlign: "center", padding: "40px" }}>
							<Spin />
						</div>
					) : (
						<table className="crm-table">
							<thead>
								<tr>
									<th>Sana</th>
									<th>Turi</th>
									<th style={{ textAlign: "right" }}>Summa</th>
									<th style={{ textAlign: "right" }}>Qoldiq</th>
									<th>Izoh</th>
								</tr>
							</thead>
							<tbody>
								<TransactionRows transactions={transactions} />
							</tbody>
						</table>
					)}
				</div>
				<div className="crm-table-foot">
					<span>Jami {totalTx} ta harakat</span>
					{totalPages > 1 && (
						<div className="crm-pager">
							<button
								type="button"
								disabled={currentPage === 1}
								onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
							>
								‹
							</button>
							{Array.from({ length: totalPages }).map((_, i) => (
								<button
									key={`page-${i + 1}`}
									className={currentPage === i + 1 ? "active" : ""}
									type="button"
									onClick={() => setCurrentPage(i + 1)}
								>
									{i + 1}
								</button>
							))}
							<button
								type="button"
								disabled={currentPage === totalPages}
								onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
							>
								›
							</button>
						</div>
					)}
				</div>
			</div>

			{/* Topup Modal */}
			<Modal
				cancelText="Bekor"
				okText="To'ldirish"
				confirmLoading={topupMutation.isPending}
				open={topupOpen}
				title="Balansni to'ldirish"
				width={480}
				onCancel={() => {
					setTopupOpen(false);
					form.resetFields();
				}}
				onOk={handleTopup}
			>
				<Form
					form={form}
					initialValues={{ method: "bank", amount: 100000 }}
					layout="vertical"
					style={{ marginTop: 16 }}
				>
					<Form.Item
						label="Summa (so'm)"
						name="amount"
						rules={[
							{ required: true, message: "Summani kiriting" },
							{
								type: "number",
								min: 1,
								message: "Summa 0 dan katta bo'lishi kerak",
							},
						]}
					>
						<InputNumber
							formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
							parser={(value) => Number(value?.replace(/\s/g, "") || "0")}
							style={{ width: "100%" }}
						/>
					</Form.Item>

					<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
						<Form.Item
							label="To'lov usuli"
							name="method"
							rules={[{ required: true, message: "Usulni tanlang" }]}
						>
							<Select options={topupMethodOptions} />
						</Form.Item>

						<Form.Item label="Hujjat raqami" name="documentNumber">
							<Input placeholder="PLT-20260601-001" />
						</Form.Item>
					</div>

					<Form.Item label="Izoh" name="note">
						<Input.TextArea placeholder="Iyun oyi uchun SMS" rows={2} />
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
}

export default SmsBalansPage;
